import { downloadFile } from "@/lib/roleContext";
import { BandChangeChip, DumbbellLegend, DumbbellTrack, PURPLE, TEAL } from "@/components/Dumbbell";
import { pickPortfolio } from "./selectedPortfolio";
import React, { useEffect, useState } from "react";
import { API, fmtScore } from "./shared";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, GitCompare, ChevronDown, ChevronRight, TrendingUp, TrendingDown, Minus, Sparkles, FileDown, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";
import GapTrend from "./GapTrend";
import { ScoringVersionNote } from "@/components/ScoringVersionNote";

const scoreCls = (s) => {
  if (s === null || s === undefined) return "text-slate-400";
  if (s >= 80) return "text-emerald-600";
  if (s >= 60) return "text-blue-600";
  if (s >= 40) return "text-amber-600";
  return "text-red-600";
};

// variance = portfolio - school. Positive → portfolio rates HIGHER than the school's self-view.
const VarianceBadge = ({ v }) => {
  if (v === null || v === undefined) return <span className="text-slate-400 text-xs">—</span>;
  const abs = Math.abs(v);
  if (abs < 0.05) return <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Minus className="h-3 w-3" /> Aligned</span>;
  const up = v > 0;
  const cls = up ? "text-blue-700 bg-blue-50 border-blue-200" : "text-amber-700 bg-amber-50 border-amber-200";
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${cls}`}>
      <Icon className="h-3 w-3" /> {up ? "+" : ""}{v} pts
    </span>
  );
};

const CMP_SERIES = [
  { key: "school", label: "School", shape: "circle", color: TEAL },
  { key: "portfolio", label: "Portfolio", shape: "diamond", color: PURPLE },
];
// Single-row School vs Portfolio comparison on a shared 0–100 axis (replaces stacked bars).
const Bars = ({ school, portfolio }) => (
  <div className="flex items-center gap-3">
    <div className="min-w-0 flex-1"><DumbbellTrack values={{ school, portfolio }} series={CMP_SERIES} refKey="school" /></div>
    <span className="hidden w-32 shrink-0 text-right sm:block"><BandChangeChip from={school} to={portfolio} /></span>
  </div>
);

export default function PeriodComparison() {
  const { periodId } = useParams();
  const navigate = useNavigate();
  const [pid, setPid] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const meR = await fetch(`${API}/api/portfolio/me`, { credentials: "include" });
        const me = await meR.json();
        const p = pickPortfolio(me.portfolios);
        if (!p) { setLoading(false); return; }
        setPid(p.id);
        const r = await fetch(`${API}/api/portfolio/${p.id}/periods/${periodId}/comparison`, { credentials: "include" });
        if (r.ok) { const d = await r.json(); setData(d); setAi(d.ai_summary || null); }
      } finally { setLoading(false); }
    })();
  }, [periodId]);

  const generateAi = async () => {
    if (!pid) return;
    setAiLoading(true);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/periods/${periodId}/comparison/ai-explanation`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not generate the AI summary");
      setAi(d);
      toast.success("AI summary ready");
    } catch (e) { toast.error(e.message); } finally { setAiLoading(false); }
  };

  const downloadPdf = () => {
    if (!pid) return;
    downloadFile(`${API}/api/portfolio/${pid}/periods/${periodId}/comparison.pdf`, "period-comparison.pdf").catch((e) => toast.error(e.message));
  };

  const downloadOneOnOne = (tenantId) => {
    if (!pid) return;
    downloadFile(`${API}/api/portfolio/${pid}/periods/${periodId}/schools/${tenantId}/one-on-one.pdf`, "one-on-one.pdf").catch((e) => toast.error(e.message));
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;
  if (!data) return <div className="py-16 text-center text-slate-500" data-testid="comparison-empty">Comparison unavailable.</div>;

  const { period, pillars, schools, aggregate } = data;
  const anyBoth = schools.some((s) => s.both_complete);

  return (
    <div className="max-w-6xl mx-auto overflow-x-hidden" data-testid="period-comparison-page">
      <button onClick={() => navigate("/portfolio/periods")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="comparison-back">
        <ArrowLeft className="h-4 w-4" /> Back to Diagnostic Periods
      </button>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-1">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg grid place-items-center bg-[#0b1220] text-white shrink-0"><GitCompare className="h-5 w-5" /></div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-slate-800">School vs Portfolio</h1>
            <p className="text-sm text-slate-500 truncate">{period.name}</p>
          </div>
        </div>
        {anyBoth && (
          <div className="flex items-center flex-wrap gap-2">
            <button onClick={generateAi} disabled={aiLoading} data-testid="comparison-ai-generate"
              className="inline-flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white px-3 py-2 disabled:opacity-60">
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : ai ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              {ai ? "Regenerate AI Summary" : "Generate AI Summary"}
            </button>
            <button onClick={downloadPdf} data-testid="comparison-pdf-download"
              className="inline-flex items-center gap-1.5 text-sm font-medium rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2">
              <FileDown className="h-4 w-4" /> Download PDF
            </button>
          </div>
        )}
      </div>

      {!period.enable_portfolio_assessments && (
        <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="comparison-not-enabled">
          Portfolio self-assessment isn't enabled for this period, so there's nothing to compare yet.
        </div>
      )}

      {/* Aggregate overview */}
      {anyBoth && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-1" data-testid="comparison-aggregate-overall">
            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Portfolio-wide average</div>
            <div className="mt-3 space-y-3">
              <div>
                <div className="text-[11px] text-slate-500">School self-view</div>
                <div className={`text-3xl font-bold ${scoreCls(aggregate.school_overall_avg)}`}>{fmtScore(aggregate.school_overall_avg)}</div>
              </div>
              <div>
                <div className="text-[11px] text-[#007a9e]">Portfolio view</div>
                <div className={`text-3xl font-bold ${scoreCls(aggregate.portfolio_overall_avg)}`}>{fmtScore(aggregate.portfolio_overall_avg)}</div>
              </div>
              <div className="pt-1 text-xs text-slate-500">{aggregate.schools_both_complete} school{aggregate.schools_both_complete === 1 ? "" : "s"} with both assessments complete</div>
              {aggregate.mixed_scoring_versions && <ScoringVersionNote testId="comparison-scoring-version-note" />}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="comparison-aggregate-pillars">
            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">Per-pillar variance (avg across schools)</div>
            <div className="mb-3"><DumbbellLegend series={CMP_SERIES} /></div>
            <div className="space-y-2.5">
              {aggregate.by_pillar.filter((p) => p.school_avg !== null || p.portfolio_avg !== null).map((p) => (
                <div key={p.pillar_id} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                  <span className="text-xs text-slate-600 sm:w-40 sm:truncate sm:shrink-0">{p.title}</span>
                  <div className="flex items-center gap-3 sm:contents">
                    <div className="flex-1 min-w-0"><Bars school={p.school_avg} portfolio={p.portfolio_avg} /></div>
                    <span className="w-20 text-right shrink-0"><VarianceBadge v={p.variance} /></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI summary */}
      {ai && (ai.overall_note || (ai.insights || []).length > 0) && (
        <div className="mt-6 rounded-xl border border-[#bfe3ee] bg-[#e6f4f8]/60 p-5" data-testid="comparison-ai-summary">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-[#007a9e]" />
            <h2 className="text-base font-semibold text-slate-800">AI summary</h2>
            {ai.generated_at && <span className="text-[11px] text-slate-400">generated {new Date(ai.generated_at).toLocaleString()}</span>}
          </div>
          {ai.overall_note && <p className="text-sm text-slate-700 leading-relaxed" data-testid="comparison-ai-note">{ai.overall_note}</p>}
          {(ai.insights || []).length > 0 && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {ai.insights.map((ins, i) => (
                <div key={i} className="rounded-lg bg-white border border-[#d6ecf4] p-3" data-testid={`comparison-ai-insight-${i}`}>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-slate-800">{ins.title}</span>
                    {ins.scope && <span className="text-[10px] uppercase tracking-wide text-[#007a9e] bg-[#e6f4f8] rounded-full px-2 py-0.5 self-start max-w-full truncate">{ins.scope}</span>}
                  </div>
                  <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">{ins.detail}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Per-school breakdown */}
      <div className="mt-8 space-y-3">
        <h2 className="text-base font-semibold text-slate-700">By school</h2>
        {schools.map((s) => {
          const isOpen = open[s.tenant_id];
          return (
            <div key={s.tenant_id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`comparison-school-${s.tenant_id}`}>
              <button onClick={() => setOpen((o) => ({ ...o, [s.tenant_id]: !o[s.tenant_id] }))}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50" data-testid={`comparison-school-toggle-${s.tenant_id}`}>
                {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="font-medium text-slate-800 truncate sm:flex-1">{s.school_name}</span>
                  {s.both_complete ? (
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
                      <span className="text-sm"><span className="text-slate-400 text-xs">School</span> <span className={`font-semibold ${scoreCls(s.school.overall)}`}>{fmtScore(s.school.overall)}</span></span>
                      <span className="text-sm"><span className="text-[#007a9e] text-xs">Portfolio</span> <span className={`font-semibold ${scoreCls(s.portfolio.overall)}`}>{fmtScore(s.portfolio.overall)}</span></span>
                      <VarianceBadge v={s.overall_variance} />
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400" data-testid={`comparison-school-pending-${s.tenant_id}`}>
                      {s.school ? "School done · " : "School pending · "}{s.portfolio ? "Portfolio done" : "Portfolio pending"}
                    </span>
                  )}
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                  {s.both_complete ? (
                    <>
                      <div className="mb-3 flex justify-end">
                        <button onClick={() => downloadOneOnOne(s.tenant_id)} data-testid={`comparison-1on1-${s.tenant_id}`}
                          className="inline-flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#0b1220] hover:bg-[#1e293b] text-white px-3 py-2">
                          <FileText className="h-4 w-4" /> 1:1 Meeting Report
                        </button>
                      </div>
                      {s.biggest_gaps.length > 0 && (
                        <div className="mb-3">
                          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Largest gaps</div>
                          <div className="flex flex-wrap gap-2">
                            {s.biggest_gaps.map((g) => (
                              <span key={g.pillar_id} className="text-xs rounded-md bg-slate-50 border border-slate-200 px-2 py-1">
                                {g.title}: <span className="font-medium">{fmtScore(g.school_score)}→{fmtScore(g.portfolio_score)}</span> <VarianceBadge v={g.variance} />
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="space-y-2.5">
                        {pillars.map((p) => (
                          <div key={p.id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                            <span className="text-xs text-slate-600 sm:w-40 sm:truncate sm:shrink-0">{p.title}</span>
                            <div className="flex-1 min-w-0"><Bars school={s.school.by_pillar[p.id]} portfolio={s.portfolio.by_pillar[p.id]} /></div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">Both the school's and the portfolio's assessments must be complete to compare. {s.portfolio ? "" : "Start the portfolio assessment from the Diagnostic Periods page."}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {schools.length === 0 && <p className="text-sm text-slate-400">Launch this period to request assessments first.</p>}
      </div>

      {pid && (
        <div className="mt-10">
          <GapTrend pid={pid} title="Gap over time" />
        </div>
      )}
    </div>
  );
}
